import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home, Users, Calendar, Trophy, BarChart3, LogOut, Menu, X, Shield, Award, Settings
} from 'lucide-react';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Base navigation items
  const baseNavItems = [
    { path: '/admin/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/admin/members', icon: Users, label: 'Members' },
    { path: '/admin/events', icon: Calendar, label: 'Events' },
    { path: '/admin/leaderboard', icon: Trophy, label: 'Leaderboard' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/certificates', icon: Award, label: 'Certificates' },
  ];

  // Add Admin Users and Settings for admins
  const navItems = user?.role === 'super_admin' || user?.role === 'admin'
    ? [
        ...baseNavItems.slice(0, 2), // Dashboard and Members
        { path: '/admin/users', icon: Shield, label: 'Admin Users' },
        ...baseNavItems.slice(2), // Events, Leaderboard, Analytics
        { path: '/admin/settings', icon: Settings, label: 'Settings' },
      ]
    : baseNavItems;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-[70px]">
          {/* Logo */}
          <Link to="/admin/dashboard" className="flex items-center no-underline shrink-0">
            <div className="bg-[#0D5128] p-1.5 rounded-xl flex items-center justify-center">
              <img src="/website-04.png" alt="Hammad Foundation" className="h-11 w-auto object-contain" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg no-underline text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-primary-100 text-primary-600'
                      : 'text-secondary-600 hover:bg-secondary-50'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Info & Logout */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0D5128] text-white flex items-center justify-center font-semibold text-sm">
                {user?.fullName?.charAt(0)?.toUpperCase()}
              </div>
              <div className="text-sm font-medium text-secondary-700 hidden lg:block">
                {user?.fullName}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 border-0 bg-transparent text-secondary-600 cursor-pointer rounded-lg hover:bg-secondary-50 transition-all hidden sm:flex items-center justify-center"
              title="Logout"
            >
              <LogOut size={18} />
            </button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 border-0 bg-transparent text-secondary-600 cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden flex flex-col gap-2 py-4 border-t border-secondary-200">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg no-underline text-sm font-medium ${
                    isActive
                      ? 'bg-primary-100 text-primary-600'
                      : 'text-secondary-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <button
              onClick={() => {
                handleLogout();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-secondary-600 bg-transparent border-0 cursor-pointer text-left hover:bg-secondary-50"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
